import type { AppState } from '../core/types';
import type { ClassificationConfig, LegendConfig, LegendFontFamily, LegendPosition } from './config';
import { defaultLegendConfig } from './config';
import { secondaryTheme } from './dualTheme';

/**
 * «Diseño de leyenda» (V1.9.0): the one writer of the legend design. The on-screen legend and every PNG read the same
 * `classificationConfig.legend`; this module validates each change, keeps the active thematic scheme in step and
 * never touches categories' source values, the Maestro or the Excel.
 */

/** Numeric limits of the editor (also used to validate stored or pasted values). */
export const LEGEND_LIMITS = {
  width: { min: 170, max: 460, step: 10 },
  scale: { min: 0.6, max: 1.6, step: 0.05 },
  fontSize: { min: 7, max: 20, step: 1 },
  swatchSize: { min: 8, max: 28, step: 1 },
  rowGap: { min: 0, max: 16, step: 1 },
  columnGap: { min: 2, max: 28, step: 1 },
  padding: { min: 4, max: 24, step: 1 },
  backgroundOpacity: { min: 0, max: 1, step: 0.05 },
  decimals: { min: 0, max: 4, step: 1 },
  borderWidth: { min: 0, max: 3, step: 0.5 },
} as const satisfies Partial<Record<keyof LegendConfig, { min: number; max: number; step: number }>>;

export type LegendNumberField = keyof typeof LEGEND_LIMITS;

export const LEGEND_FONTS: Readonly<Record<LegendFontFamily, { readonly label: string; readonly css: string; readonly canvas: string }>> = {
  // `system` keeps exactly the V1.8 rendering (inherited UI font on screen, system-ui on the PNG).
  system: { label: 'Sistema', css: 'inherit', canvas: 'system-ui, sans-serif' },
  serif: { label: 'Serif', css: 'Georgia, "Times New Roman", serif', canvas: 'Georgia, "Times New Roman", serif' },
  condensed: { label: 'Estrecha', css: '"Arial Narrow", "Roboto Condensed", "Liberation Sans Narrow", sans-serif', canvas: '"Arial Narrow", "Roboto Condensed", "Liberation Sans Narrow", sans-serif' },
};

const POSITIONS: readonly LegendPosition[] = ['top-left', 'top-right', 'bottom-left', 'bottom-right', 'free'];
const TITLE_MAX = 80;
const HEX = /^#[0-9a-f]{6}$/i;

const clamp = (value: number, field: LegendNumberField): number => {
  const { min, max } = LEGEND_LIMITS[field];
  return Math.min(max, Math.max(min, value));
};

/** A validated patch: out-of-range numbers are clamped, unknown enum values and invalid colours are dropped. */
export function sanitizeLegendPatch(patch: Partial<LegendConfig>): Partial<LegendConfig> {
  const next: Record<string, unknown> = {};
  (Object.keys(patch) as (keyof LegendConfig)[]).forEach((key) => {
    const value = patch[key];
    if (key in LEGEND_LIMITS) { if (typeof value === 'number' && Number.isFinite(value)) next[key] = clamp(value, key as LegendNumberField); return; }
    if (key === 'title' || key === 'remainderLabel') { if (typeof value === 'string') next[key] = value.slice(0, TITLE_MAX); return; }
    if (key === 'backgroundColor' || key === 'borderColor') { if (typeof value === 'string' && HEX.test(value)) next[key] = value; return; }
    if (key === 'position') { if (POSITIONS.includes(value as LegendPosition)) next[key] = value; return; }
    if (key === 'fontFamily') { if (typeof value === 'string' && value in LEGEND_FONTS) next[key] = value; return; }
    if (key === 'areaUnit') { if (value === 'ha' || value === 'mz') next[key] = value; return; }
    if (key === 'layout') { if (value === 'flat' || value === 'grouped') next[key] = value; return; }
    if (key === 'metricsAlignment') { if (value === 'start' || value === 'end') next[key] = value; return; }
    if (key === 'x' || key === 'y') { if (typeof value === 'number' && Number.isFinite(value)) next[key] = Math.max(0, value); return; }
    if (typeof value === 'boolean') next[key] = value;
  });
  return next as Partial<LegendConfig>;
}

type ClassificationPatch = Pick<AppState, 'classificationConfig' | 'thematicSchemes' | 'legendConfig'>;

/** Store patch for a new classification: the active scheme and the legend mirror follow it. */
export function commitClassification(state: AppState, config: ClassificationConfig): ClassificationPatch {
  return { classificationConfig: config, thematicSchemes: state.thematicSchemes.map((item) => item.id === config.id ? config : item), legendConfig: config.legend };
}

/** Store patch for a legend design change (validated). An empty title falls back to the default one. */
export function updateLegendDesign(state: AppState, patch: Partial<LegendConfig>): ClassificationPatch {
  const clean = sanitizeLegendPatch(patch);
  const legendPatch = clean.title !== undefined && !clean.title.trim() ? { ...clean, title: defaultLegendConfig.title } : clean;
  const config = state.classificationConfig;
  return commitClassification(state, { ...config, legend: { ...config.legend, ...legendPatch } });
}

/**
 * Visible title of the secondary (outline) dimension, e.g. «Nombre Hacienda» → «Hacienda». Only the legend title
 * changes: the source field, its values and the split table keep their names.
 */
export function updateSecondaryTitle(state: AppState, title: string): ClassificationPatch | null {
  const config = state.classificationConfig; const secondary = config.secondary;
  const name = title.trim().slice(0, TITLE_MAX);
  if (!secondary || !name) return null;
  return commitClassification(state, { ...config, secondary: { ...secondary, name } });
}

/** Categories in the legend order chosen by the user (values not in the list keep their own order, after it). */
export function legendOrdered<T extends { readonly value: string }>(items: readonly T[], order: readonly string[] | undefined): readonly T[] {
  if (!order?.length) return items;
  const rank = new Map(order.map((value, index) => [value, index]));
  return items.map((item, index) => ({ item, index })).sort((a, b) => (rank.get(a.item.value) ?? order.length + a.index) - (rank.get(b.item.value) ?? order.length + b.index)).map(({ item }) => item);
}

/**
 * Legend order of one category, one step up or down. Stored in the legend (`categoryOrder` / `secondaryOrder`), so a
 * rebuild of the categories (Ver por, ámbito, filtros) keeps it; the map, the counts and the source values do not change.
 */
export function moveLegendCategory(state: AppState, axis: 'principal' | 'secondary', value: string, delta: -1 | 1): ClassificationPatch | null {
  const config = state.classificationConfig;
  // The secondary list is the one drawn (stored categories plus any value new since they were built).
  const categories = axis === 'principal' ? config.categories : config.secondary ? [...(secondaryTheme(state)?.categories.values() ?? config.secondary.categories)] : undefined;
  if (!categories) return null;
  const key = axis === 'principal' ? 'categoryOrder' : 'secondaryOrder';
  const order = legendOrdered(categories, config.legend[key]).map((category) => category.value);
  const index = order.indexOf(value); const target = index + delta;
  if (index < 0 || target < 0 || target >= order.length) return null;
  [order[index], order[target]] = [order[target]!, order[index]!];
  return commitClassification(state, { ...config, legend: { ...config.legend, [key]: order } });
}

const stringList = (value: unknown): readonly string[] | undefined => Array.isArray(value) ? [...new Set(value.filter((item): item is string => typeof item === 'string').map((item) => item.slice(0, 200)))].slice(0, 500) : undefined;

/** Stored legend (project file): defaults for absent fields, validated V1.9 fields; older values are kept as they are. */
export function normalizeLegendConfig(value: Partial<LegendConfig> | undefined): LegendConfig {
  const { categoryOrder, secondaryOrder, ...rest } = { ...defaultLegendConfig, ...value } as LegendConfig;
  const fontFamily = rest.fontFamily in LEGEND_FONTS ? rest.fontFamily : defaultLegendConfig.fontFamily;
  const borderWidth = typeof rest.borderWidth === 'number' && Number.isFinite(rest.borderWidth) ? clamp(rest.borderWidth, 'borderWidth') : defaultLegendConfig.borderWidth;
  const order = stringList(categoryOrder); const second = stringList(secondaryOrder);
  const { layout, showSubtotals, showTotal, showUnassigned, ...base } = rest;
  return {
    ...base, fontFamily, borderWidth, ...(order?.length ? { categoryOrder: order } : {}), ...(second?.length ? { secondaryOrder: second } : {}),
    ...(layout === 'grouped' ? { layout } : {}), ...(showSubtotals === false ? { showSubtotals } : {}), ...(showTotal === false ? { showTotal } : {}), ...(showUnassigned === false ? { showUnassigned } : {}),
  };
}
